import React, { useState, useMemo } from 'react';
import { formatPrice } from '../../utils/helpers';
import './AiPositionsTable.css';

const REASON_CONFIG = {
  manual: { label: 'Exit', className: 'reason-ai' },
  ai_decision: { label: 'Exit', className: 'reason-ai' },
  stop_loss: { label: 'SL Hit', className: 'reason-sl' },
  take_profit: { label: 'TP Hit', className: 'reason-tp' },
  liquidation: { label: 'Liq', className: 'reason-liq' },
  challenge_wiped: { label: 'Wiped', className: 'reason-liq' },
};

function findDecisionForPosition(position, decisions) {
  if (!decisions || decisions.length === 0) return null;

  // First try matching by decisionId (most reliable)
  if (position.decisionId) {
    const byId = decisions.find(d =>
      String(d._id) === String(position.decisionId)
    );
    if (byId) return byId;
  }

  // Fallback: match trade_decisions by symbol + closest time
  const tradeDecisions = decisions.filter(d =>
    d.type === 'trade_decision' && d.action &&
    d.action.symbol === position.symbol
  );

  if (tradeDecisions.length === 0) return null;

  const openedAt = new Date(position.openedAt).getTime();
  let closest = null;
  let minDiff = Infinity;

  for (const d of tradeDecisions) {
    const diff = Math.abs(new Date(d.createdAt).getTime() - openedAt);
    if (diff < minDiff) {
      minDiff = diff;
      closest = d;
    }
  }

  return closest;
}

function PositionRow({ position, decisions, isHistory, gridClass }) {
  const [expanded, setExpanded] = useState(false);

  const pnl = isHistory ? (position.realizedPnl || 0) : (position.unrealizedPnl || 0);
  const pnlPct = position.pnlPercent || 0;
  const pnlClass = pnl > 0 ? 'positive' : pnl < 0 ? 'negative' : '';

  const decision = useMemo(() => findDecisionForPosition(position, decisions), [position, decisions]);

  const entryPrice = formatPrice(position.entryPrice);
  const markPrice = isHistory
    ? formatPrice(position.exitPrice)
    : formatPrice(position._currentPrice || position.currentPrice || position.entryPrice);
  const liqPrice = position.liquidationPrice ? formatPrice(position.liquidationPrice) : '-';
  const slPrice = position.stopLoss ? formatPrice(position.stopLoss) : '-';
  const tpPrice = position.takeProfit ? formatPrice(position.takeProfit) : '-';
  const margin = position.margin ? `$${position.margin.toFixed(2)}` : '-';

  const reason = isHistory && position.closeReason
    ? REASON_CONFIG[position.closeReason] || { label: position.closeReason, className: '' }
    : null;

  return (
    <div className="ai-position-row" onClick={() => setExpanded(!expanded)}>
      <div className={`ai-position-row-main ${gridClass}`}>
        {/* Symbol */}
        <div className="ai-pos-symbol-cell">
          <div className={`ai-pos-side-bar ${position.side}`} />
          <div className="ai-pos-symbol-info">
            <span className="ai-pos-symbol-name">{position.symbol}</span>
            <span className="ai-pos-symbol-meta">
              <span className={`side-${position.side}`}>
                {position.side === 'long' ? 'Long' : 'Short'}
              </span>
              {position.leverage && ` ${position.leverage}x`}
            </span>
          </div>
        </div>

        {/* Entry */}
        <div className="ai-pos-value">{entryPrice}</div>

        {/* Mark / Exit */}
        <div className="ai-pos-value">{markPrice}</div>

        {/* SL / TP (open positions only) */}
        {!isHistory && (
          <div className="ai-pos-sltp-cell">
            <span className="ai-pos-sl">{slPrice}</span>
            <span className="ai-pos-tp">{tpPrice}</span>
          </div>
        )}

        {/* Liq / Close Reason */}
        {isHistory ? (
          <div className="ai-pos-value">
            {reason ? (
              <span className={`close-reason ${reason.className}`}>{reason.label}</span>
            ) : '-'}
          </div>
        ) : (
          <div className="ai-pos-value liq">{liqPrice}</div>
        )}

        {/* Margin */}
        <div className="ai-pos-value margin">{margin}</div>

        {/* PnL */}
        <div className="ai-pos-pnl-cell">
          <div className={`ai-pos-pnl-value ${pnlClass}`}>
            {pnl >= 0 ? '+$' : '-$'}{Math.abs(pnl).toFixed(2)}
          </div>
          <div className={`ai-pos-roi ${pnlClass}`}>
            {pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%
          </div>
        </div>
      </div>

      {/* Expandable Tooltip */}
      {expanded && (
        <div className="ai-position-tooltip">
          <div className="ai-position-tooltip-header">
            <svg className="ai-position-tooltip-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span className="ai-position-tooltip-label">AI Reasoning</span>
          </div>
          <div className="ai-position-tooltip-text">
            {decision && (decision.reasoning || decision.logMessage)
              ? (decision.reasoning || decision.logMessage)
              : position.reasoning
                ? position.reasoning
                : 'No reasoning available for this trade'}
          </div>
        </div>
      )}
    </div>
  );
}

function AiPositionsTable({ positions, closedTrades, trades, decisions }) {
  const [activeTab, setActiveTab] = useState('open');

  const totalUPnl = useMemo(() => {
    if (!positions || positions.length === 0) return 0;
    return positions.reduce((sum, p) => sum + (p.unrealizedPnl || 0), 0);
  }, [positions]);

  const upnlClass = totalUPnl > 0 ? 'positive' : totalUPnl < 0 ? 'negative' : '';

  // Combine closedTrades and trades for history, deduplicating
  const historyTrades = useMemo(() => {
    const allTrades = [...(closedTrades || [])];
    if (trades) {
      for (const t of trades) {
        if (!allTrades.some(ct => String(ct._id) === String(t._id))) {
          allTrades.push(t);
        }
      }
    }
    return allTrades.sort((a, b) => {
      const da = new Date(a.closedAt || a.openedAt);
      const db = new Date(b.closedAt || b.openedAt);
      return db - da;
    });
  }, [closedTrades, trades]);

  const openCount = positions ? positions.length : 0;
  const historyCount = historyTrades.length;

  const gridHeaders = activeTab === 'open'
    ? ['Symbol', 'Entry', 'Mark', 'SL / TP', 'Liq', 'Margin', 'PnL']
    : ['Symbol', 'Entry', 'Exit', 'Reason', 'Margin', 'PnL'];

  const gridClass = activeTab === 'open' ? 'open-grid' : 'history-grid';

  const activeList = activeTab === 'open' ? (positions || []) : historyTrades;

  return (
    <div className="ai-positions-panel" data-tour="ai-positions-table">
      {/* Header with Tabs */}
      <div className="ai-positions-header">
        <div className="ai-positions-tabs">
          <button
            className={`ai-positions-tab ${activeTab === 'open' ? 'active' : ''}`}
            onClick={() => setActiveTab('open')}
          >
            Open Positions
            <span className="ai-positions-tab-count">{openCount}</span>
          </button>
          <button
            className={`ai-positions-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
            <span className="ai-positions-tab-count">{historyCount}</span>
          </button>
        </div>

        {activeTab === 'open' && openCount > 0 && (
          <div className={`ai-positions-upnl ${upnlClass}`}>
            uPnL: {totalUPnl >= 0 ? '+' : ''}{totalUPnl.toFixed(2)}
          </div>
        )}
      </div>

      {/* Grid Header */}
      <div className={`ai-positions-grid-header ${gridClass}`}>
        {gridHeaders.map(h => <div key={h}>{h}</div>)}
      </div>

      {/* Rows */}
      <div className="ai-positions-scroll">
        {activeList.length === 0 ? (
          <div className="ai-positions-empty">
            {activeTab === 'open' ? 'No open positions' : 'No trade history yet'}
          </div>
        ) : (
          activeList.map((item) => (
            <PositionRow
              key={item._id}
              position={item}
              decisions={decisions}
              isHistory={activeTab === 'history'}
              gridClass={gridClass}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default AiPositionsTable;
